const ALLOWED_TAGS = new Set([
  'br',
  'div',
  'p',
  'figure',
  'img',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'span',
  'blockquote',
  'a',
  'font',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  's',
  'strike',
  'mark',
  'small',
  'code',
  'pre',
  'sub',
  'sup',
  'center',
]);

/** style 속성 내 일반적인 XSS 패턴 차단 (완화된 화이트리스트 대신) */
const DANGEROUS_STYLE_PATTERN =
  /(url\s*\(|expression\s*\(|javascript\s*:|@import|@-moz-binding|-moz-binding|behavior\s*:|\\0|<\/|<!--|<script)/i;

function isSafeInlineStyle(value: string): boolean {
  if (!value || value.length > 8000) return false;
  // 붙여넣기·contentEditable이 Tailwind 유틸 변수를 인라인 style로 실을 수 있음 — 저장·표시 모두 불필요
  if (/--tw-/i.test(value)) return false;
  return !DANGEROUS_STYLE_PATTERN.test(value);
}

function isSafeHref(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return false;
  if (v.startsWith('javascript:') || v.startsWith('vbscript:')) return false;
  if (v.startsWith('data:')) return false;
  return true;
}

function keepAttribute(tag: string, attr: string, value: string): boolean {
  const a = attr.toLowerCase();

  if (a === 'class') return true;
  if (a === 'style') return isSafeInlineStyle(value);

  if (tag === 'img') {
    if (a === 'src') return !value.trim().toLowerCase().startsWith('javascript:');
    if (a === 'alt' || a === 'data-embed' || a === 'width' || a === 'height' || a === 'loading' || a === 'decoding') {
      return true;
    }
    return false;
  }

  if (tag === 'font') {
    return a === 'color' || a === 'face' || a === 'size';
  }

  if (tag === 'figure') {
    return a === 'data-embed' || a === 'contenteditable';
  }

  if (tag === 'a') {
    if (a === 'href') return isSafeHref(value);
    if (a === 'target') {
      return /^(_blank|_self|_parent|_top)$/i.test(value.trim());
    }
    if (a === 'rel' || a === 'title') return true;
    return false;
  }

  if (
    a === 'align' &&
    ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'center'].includes(tag)
  ) {
    return /^(left|right|center|justify)$/i.test(value.trim());
  }

  return false;
}

const removeNodeKeepChildren = (element: Element) => {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
};

const sanitizeAllowedAttributes = (element: Element) => {
  const tag = element.tagName.toLowerCase();
  const attrs = Array.from(element.attributes);

  attrs.forEach(({ name, value }) => {
    const attr = name.toLowerCase();
    if (!keepAttribute(tag, attr, value)) {
      element.removeAttribute(name);
    }
  });
};

export const sanitizeProjectDescHtml = (html: string): string => {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((node) => node.remove());

  Array.from(doc.body.querySelectorAll('*')).forEach((element) => {
    const tag = element.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      removeNodeKeepChildren(element);
      return;
    }
    sanitizeAllowedAttributes(element);
  });

  return doc.body.innerHTML;
};

const STORY_FIGURE_TOKEN_PREFIX = '__MF_STORY_FIGURE__';
const STORY_BR_TOKEN_PREFIX = '__MF_STORY_BR__';

const escapeHtmlForEditor = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

export const hasLegacyStoryMarkup = (html: string): boolean => {
  if (!html) return false;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  return Array.from(doc.body.querySelectorAll('*')).some((element) => {
    const tag = element.tagName.toLowerCase();

    if (tag === 'br') return false;
    if (tag === 'figure' && element.getAttribute('data-embed') === 'story-image') return false;
    if (tag === 'img' && element.closest('figure[data-embed="story-image"]')) return false;

    return true;
  });
};

export const prepareStoryHtmlForEditor = (html: string): string => {
  if (!html) return '';

  const figures: string[] = [];
  const brs: string[] = [];

  let working = html.replace(
    /<figure\b[^>]*data-embed=(["'])story-image\1[^>]*>[\s\S]*?<\/figure>/gi,
    (match) => {
      const index = figures.push(match) - 1;
      return `${STORY_FIGURE_TOKEN_PREFIX}${index}__`;
    },
  );

  working = working.replace(/<br\s*\/?>/gi, () => {
    const index = brs.push('<br>') - 1;
    return `${STORY_BR_TOKEN_PREFIX}${index}__`;
  });

  working = escapeHtmlForEditor(working);

  working = working.replace(
    new RegExp(`${STORY_FIGURE_TOKEN_PREFIX}(\\d+)__`, 'g'),
    (_, index) => figures[Number(index)] ?? '',
  );
  working = working.replace(
    new RegExp(`${STORY_BR_TOKEN_PREFIX}(\\d+)__`, 'g'),
    (_, index) => brs[Number(index)] ?? '<br>',
  );

  return working;
};

export const restoreStoryHtmlFromEditor = (html: string): string => {
  if (!html) return '';
  return decodeHtmlEntitiesFully(html.replace(/\u00a0/g, ' '));
};

const decodeEscapedHtmlOnce = (html: string): string => {
  return html
    .replace(/&nbsp;/gi, ' ')
    // &amp; 먼저 — &amp;lt; 등 이중 인코딩 풀기
    .replace(/&amp;/gi, '&')
    .replace(/&#(\d{1,7});/g, (m, n) => {
      const c = parseInt(n, 10);
      return c >= 0 && c <= 0x10ffff ? String.fromCodePoint(c) : m;
    })
    .replace(/&#x([0-9a-f]{1,6});/gi, (m, h) => {
      const c = parseInt(h, 16);
      return c >= 0 && c <= 0x10ffff ? String.fromCodePoint(c) : m;
    })
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
};

/** 텍스트로 이스케이프된 마크업(&lt;img …&gt; 등)을 실제 태그로 풀 때까지 반복 */
export const decodeHtmlEntitiesFully = (html: string): string => {
  if (!html) return '';
  let s = html;
  for (let i = 0; i < 12; i += 1) {
    const next = decodeEscapedHtmlOnce(s);
    if (next === s) break;
    s = next;
  }
  return s;
};

export const normalizeStoryHtmlForDisplay = (html: string): string => {
  if (!html) return '';

  let normalized = decodeHtmlEntitiesFully(html);

  normalized = normalized
    .replace(/<div\b[^>]*>\s*<br\s*\/?>\s*<\/div>/gi, '<br>')
    .replace(/<p\b[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, '<br>')
    .replace(/<\/(div|p)>/gi, '<br>')
    .replace(/<(div|p)\b[^>]*>/gi, '')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');

  return sanitizeProjectDescHtml(normalized);
};

/**
 * 소식 본문 HTML — API가 `contents`에 이스케이프·중복만 넣고 `update_message`에 원본을 두는 경우가 있어 등록 직후에도 올바르게 보이도록 `update_message`를 우선합니다.
 */
export const getProjectPostBodyHtml = (post: { update_message?: string; contents?: string }): string => {
  const um = post.update_message;
  if (typeof um === 'string' && um.trim() !== '') return um;
  const ct = post.contents;
  if (typeof ct === 'string') return ct;
  return '';
};

/** 본문에서 첫 인라인 이미지 URL (따옴표 없는 src= 형식 포함) */
export const firstImgSrcFromPostHtml = (html: string): string => {
  if (!html) return '';
  const quoted = html.match(/<img\b[^>]*\bsrc\s*=\s*["']([^"']*)["']/i);
  if (quoted?.[1]?.trim()) return quoted[1].trim();
  const bare = html.match(/<img\b[^>]*\bsrc\s*=\s*([^\s>]+)/i);
  return (bare?.[1] || '').trim();
};
